import { AccessTokenDataExtended } from 'src/state/redux/slice/authToken';
import {
  CustomDeckORM,
  DocumentVersionORM,
  isCustomDeckORM,
  isDocumentVersionORM,
  isFolderItemORM,
  ORMTypes,
  PageGroupORM,
  PageORM,
  Presentable,
  PresentableGroup,
  PresentablePage,
} from 'src/types/types';
import { v4 as uuid } from 'uuid';
import { PresentableModelORM } from './ContentProvider';
import { Notation } from '@alucio/aws-beacon-amplify/src/models';
import {
  getPageWithPageSettingsHiddenState,
  isSourceHiddenSlide,
} from 'src/utils/hiddenSlides';

function getDocumentVersionVisiblePages(
  documentVersionORM: DocumentVersionORM,
  visiblePages: number[],
  group: PresentableGroup,
  includeHiddenSlides: boolean = false,
  includeHiddenSlidesOutsideExplicitSelection: boolean = true,
): PresentablePage[] {
  let presentationPageNumber = 1;

  return documentVersionORM.meta.allPages.reduce<PresentablePage[]>((acc, page): PresentablePage[] => {
    const isExplicitlyVisible = visiblePages.includes(page.number);
    if (page.isHidden && !includeHiddenSlides && !isExplicitlyVisible) {
      return acc;
    }
    if (
      !shouldIncludePage(
        page,
        visiblePages,
        includeHiddenSlides,
        includeHiddenSlidesOutsideExplicitSelection,
      )
    ) {
      return acc;
    }
    acc.push({
      id: `${group.id}_${presentationPageNumber}`,
      page: getPageWithHiddenState(page, visiblePages, includeHiddenSlides),
      documentVersionORM,
      presentationPageNumber: presentationPageNumber++,
      presentableGroup: group,
    });
    return acc;
  }, []);
}

function shouldIncludePage(
  page: { number: number, isHidden?: boolean | null, sourceIsHidden?: boolean | null },
  visiblePages: number[],
  includeHiddenSlides: boolean,
  includeHiddenSlidesOutsideExplicitSelection: boolean = true,
): boolean {
  const isHiddenByVisiblePages = !!visiblePages.length && !visiblePages.includes(page.number);

  // Edit surfaces include hidden/source-hidden pages so users can restore them.
  return !visiblePages.length ||
    visiblePages.includes(page.number) ||
    (includeHiddenSlides &&
      includeHiddenSlidesOutsideExplicitSelection &&
      (isSourceHiddenSlide(page) || isHiddenByVisiblePages));
}

function getPageWithHiddenState<TPage extends {
  number: number,
  isHidden?: boolean | null,
  sourceIsHidden?: boolean | null,
}>(
  page: TPage,
  visiblePages: number[],
  includeHiddenSlides: boolean,
): TPage {
  if (!includeHiddenSlides) {
    if (visiblePages.includes(page.number) && isSourceHiddenSlide(page)) {
      // A user/custom deck override can show a source-hidden slide without losing provenance.
      return {
        ...page,
        isHidden: false,
        sourceIsHidden: true,
      };
    }

    return page;
  }

  if (!visiblePages.length) {
    return page;
  }

  const isHiddenByVisiblePages = !visiblePages.includes(page.number);
  if (!isSourceHiddenSlide(page) && !isHiddenByVisiblePages) {
    return page;
  }

  return {
    ...page,
    isHidden: isHiddenByVisiblePages,
    // Hidden by custom deck visibility still needs source provenance for later unhide decisions.
    sourceIsHidden: true,
  };
}

interface PresentableFromCustomDeckResponse {
  presentableGroups: PresentableGroup[],
  presentablePages: PresentablePage[],
  numberOfPages: number,
}

function setAuthToken(
  docVersion: DocumentVersionORM,
  authTokens?: AccessTokenDataExtended[],
) {
  const authToken = authTokens?.find(token =>
    token.documentVersionId === docVersion.model.id,
  );
  if (authToken) {
    return {
      ...docVersion,
      meta: {
        ...docVersion.meta,
        assets: {
          ...docVersion.meta.assets,
          accessToken: authToken.accessToken,
        },
      },
    }
  }

  return docVersion;
}

function getCustomDeckPresentableGroups(
  customDeck: CustomDeckORM,
  authTokens?: AccessTokenDataExtended[],
  visibleOverwrittenPageIds?: string[],
  includeHiddenSlides: boolean = false)
  : PresentableFromCustomDeckResponse {
  const presentableGroups: PresentableGroup[] = [];
  const presentablePages: PresentablePage[] = [];
  let presentationPageNumber = 1;

  // Keep custom deck group ids stable; player page changes depend on group identity.
  customDeck.meta.customDeckGroups.forEach((groupORM) => {
    if ((!groupORM.model.visible && !includeHiddenSlides) || !groupORM.pages.length) {
      return;
    }

    const firstDocumentVersionORM = groupORM.pages[0]?.documentVersionORM;
    if (!firstDocumentVersionORM) {
      return;
    }

    const presentableGroup: PresentableGroup = {
      id: groupORM.model.id,
      documentVersionORM: setAuthToken(firstDocumentVersionORM, authTokens),
      pages: [],
    };

    groupORM.pages.forEach((page) => {
      if (!page.documentVersionORM) return;
      const pageORM = page.documentVersionORM.relations.pages
        .find(({ model: { pageId } }) => pageId === page.model.pageId);

      const omitPage = !pageORM ||
        (pageORM.model.isHidden && !includeHiddenSlides) ||
        (visibleOverwrittenPageIds?.length && !visibleOverwrittenPageIds.includes(pageORM?.model.pageId))

      if (omitPage) {
        return;
      }

      const pageWithSourceHiddenState = getPageWithPageSettingsHiddenState({
        page: pageORM.model,
        pageSettings: page.documentVersionORM.model.pageSettings ?? [],
      });

      // Group visibility is the custom deck override; sourceIsHidden remains document-version metadata.
      const presentablePage: PresentablePage = {
        id: pageORM.model.pageId,
        page: {
          ...pageWithSourceHiddenState,
          isHidden: !groupORM.model.visible,
          sourceIsHidden: !!pageWithSourceHiddenState.sourceIsHidden,
        },
        documentVersionORM: page.documentVersionORM,
        presentationPageNumber: presentationPageNumber++,
        presentableGroup,
      };

      presentableGroup.pages.push(presentablePage);
      presentablePages.push(presentablePage);
    });

    if (presentableGroup.pages.length) {
      presentableGroups.push(presentableGroup);
    }
  });

  return {
    presentableGroups,
    presentablePages,
    numberOfPages: presentablePages.length,
  }
}

interface GetPresentableGroupsReturnType {
  groups: PresentableGroup[],
  pages: PresentablePage[],
}

/**
 * @function getPresentableGroups
 * @param pages
 * @param visiblePages
 * @param authTokens: optional
 * @param existingGroupId: optional - if pass in, return value will keep the same groupId.
 * @description Received a group of pages (can be different group/docs)
 * and returns presentable groups accordingly.
*/
function getPresentableGroups(
  pages: PageORM[],
  visiblePages: number[],
  authTokens?: AccessTokenDataExtended[],
  existingGroupId?: string,
  includeHiddenSlides: boolean = false,
): GetPresentableGroupsReturnType {
  if (!pages.length) {
    return { groups: [], pages: [] };
  }

  const presentableGroups: PresentableGroup[] = [{
    id: existingGroupId ?? uuid(),
    documentVersionORM: setAuthToken(pages[0].relations.documentVersionORM, authTokens),
    pages: [],
  }];
  const presentablePages: PresentablePage[] = [];
  let pageNumber = 1;

  // WE NEED TO HAVE INTERNAL GROUPS (SAME DOCVER) WITHOUT DUPLICATED SLIDES TO ENSURE
  // THE PLAYER'S CORRECT BEHAVIOR. THEREFORE, HERE, WE'RE CREATING GROUPS WITH NO DUPLICATED SLIDES
  pages.forEach((pageORM) => {
    if (pageORM.model.isHidden && !includeHiddenSlides) {
      return;
    }
    if (shouldIncludePage(pageORM.model, visiblePages, includeHiddenSlides)) {
      const latestPresentableGroup = presentableGroups[presentableGroups.length - 1];
      const isIncludedInLatestGroup = latestPresentableGroup.pages
        .some((existingPage) => existingPage.page.pageId === pageORM.model.pageId);
      const isDifferentDocumentVersion = latestPresentableGroup.documentVersionORM.model.id !==
        pageORM.relations.documentVersionORM.model.id;

      // TO AVOID MISBEHAVIORS OF THE PLAYER, THE SLIDES WITHIN A GROUP, MUST BE IN CONSECUTIVE ORDER
      const isPreviousSlideNumber = latestPresentableGroup.pages.some(({ page }) => pageORM.model.number < page.number);

      const newPage: PresentablePage = {
        id: pageORM.model.pageId,
        page: getPageWithHiddenState(pageORM.model, visiblePages, includeHiddenSlides),
        documentVersionORM: pageORM.relations.documentVersionORM,
        presentationPageNumber: pageNumber,
        presentableGroup: latestPresentableGroup,
      };

      // IF THE SLIDE IS ALREADY IN THE CURRENT GROUP OR BELONGS TO A DIFFERENT
      // DOCUMENT VERSION OR IS NOT CONSECUTIVE NUMBER, WE'LL CREATE A NEW GROUP
      if (isIncludedInLatestGroup || isDifferentDocumentVersion || isPreviousSlideNumber) {
        const newGroup: PresentableGroup = {
          id: uuid(),
          documentVersionORM: setAuthToken(pageORM.relations.documentVersionORM, authTokens),
          pages: [],
        };
        newPage.presentableGroup = newGroup;
        presentableGroups.push({
          ...newGroup,
          pages: [newPage],
        });
      } else {
        presentableGroups[presentableGroups.length - 1].pages.push(newPage);
      }
      pageNumber++;
      presentablePages.push(newPage);
    }
  });

  return {
    groups: presentableGroups,
    pages: presentablePages,
  }
}

function getPresentableFromDocVer(
  documentVersionORM: DocumentVersionORM,
  title: string,
  visiblePages: number[],
  notations?: Notation[],
  authTokens?: AccessTokenDataExtended[],
  includeHiddenSlides: boolean = false,
  includeHiddenSlidesOutsideExplicitSelection: boolean = true,
): Presentable {
  const groupId = uuid();
  // FIRST, WE CREATE THE GROUP, WITH AN EMPTY ARRAY OF PAGES,
  // SINCE THE PAGES NEED TO HAVE THE GROUP'S REFERENCE
  const presentableGroups: PresentableGroup[] = [{
    id: groupId,
    documentVersionORM: setAuthToken(documentVersionORM, authTokens),
    pages: [],
  }];
  // THEN, WE ADD THE PAGES WITH THE REFERENCE
  presentableGroups[0].pages =
    getDocumentVersionVisiblePages(
      documentVersionORM,
      visiblePages || [],
      presentableGroups[0],
      includeHiddenSlides,
      includeHiddenSlidesOutsideExplicitSelection,
    );
  return {
    id: uuid(),
    orm: documentVersionORM,
    title,
    numberOfPages: presentableGroups[0].pages.length,
    presentablePages: presentableGroups[0].pages,
    presentableGroups,
    notations,
  };
}

function getPresentableFromPageGroup(
  item: PageGroupORM,
  visiblePages: number[],
  visibleOverwrittenPageIds?: string[],
  authTokens?: AccessTokenDataExtended[],
  includeHiddenSlides: boolean = false,
): Presentable {
  const title = item.relations.documentVersionORM.model.title || ''
  const groupPages = visibleOverwrittenPageIds?.length
    ? item.relations.pages.filter((page) => visibleOverwrittenPageIds.includes(page.model.pageId))
    : item.relations.pages;
  const { groups, pages } = getPresentableGroups(groupPages, visiblePages, authTokens, undefined, includeHiddenSlides);

  return {
    id: uuid(),
    orm: item,
    title,
    numberOfPages: pages.length,
    presentablePages: pages,
    presentableGroups: groups,
  }
}

/**
 * @function updatePresentableDocVer
 * @param oldPresentable
 * @param updatedDocVer
 * @param authTokens: optional
 * @description Generate a presentable with updated documentVersionORM.
 * Keeping all the same groupId in presentableGroups so it will not be detected as new groups.
*/
export function updatePresentableDocVer(
  oldPresentable: Presentable,
  updatedDocVer: DocumentVersionORM,
  authTokens?: AccessTokenDataExtended[],
  includeHiddenSlides: boolean = false,
): Presentable {
  const groupId = oldPresentable.presentableGroups[0].id
  const title = updatedDocVer.model.title || ''
  const visiblePages = oldPresentable.presentableGroups[0].pages.map(page => page.page.number)
  // FIRST, WE CREATE THE GROUP, WITH AN EMPTY ARRAY OF PAGES,
  // SINCE THE PAGES NEED TO HAVE THE GROUP'S REFERENCE
  const presentableGroups: PresentableGroup[] = [{
    id: groupId,
    documentVersionORM: setAuthToken(updatedDocVer, authTokens),
    pages: [],
  }];
  // THEN, WE ADD THE PAGES WITH THE REFERENCE
  presentableGroups[0].pages =
    getDocumentVersionVisiblePages(
      updatedDocVer,
      visiblePages || [],
      presentableGroups[0],
      includeHiddenSlides,
    );

  const presentable = {
    id: oldPresentable.id,
    orm: updatedDocVer,
    title,
    numberOfPages: presentableGroups[0].pages.length,
    presentablePages: presentableGroups[0].pages,
    presentableGroups,
  };

  return {
    ...presentable,
    presentableGroups: presentable.presentableGroups.map((group) => ({
      ...group,
      pages: group.pages.map((page) => ({
        ...page,
        presentableGroup: group,
      })),
    })),
  };
}

export function getPresentable(
  item: PresentableModelORM | undefined,
  authTokens?: AccessTokenDataExtended[],
  visibleOverwrittenPageIds?: string[],
  notations?: Notation[],
  includeHiddenSlides: boolean = false,
): Presentable | undefined {
  let presentable: Presentable | undefined;
  if (!item) return presentable;

  switch (item.type) {
    case ORMTypes.DOCUMENT_VERSION: {
      const visiblePages: number[] = [];
      if (visibleOverwrittenPageIds) {
        item.meta.allPages.forEach((page) => {
          if (visibleOverwrittenPageIds.includes(page.pageId)) {
            visiblePages.push(page.number);
          }
        });
      }

      presentable = getPresentableFromDocVer(
        item,
        item.model.title || '',
        visiblePages,
        notations,
        authTokens,
        includeHiddenSlides,
        !visibleOverwrittenPageIds?.length,
      );
      break;
    }

    case ORMTypes.PAGE_GROUP:
      presentable = getPresentableFromPageGroup(item, [], visibleOverwrittenPageIds, authTokens, includeHiddenSlides);
      break

    case ORMTypes.FOLDER_ITEM:
      if (isFolderItemORM(item.relations.itemORM)) {
        throw new Error('FolderItem cannot be a folder');
      } else if (isDocumentVersionORM(item.relations.itemORM)) {
        const title = item.model.customTitle || item.relations.itemORM.model.title || '';
        const overwrittenVisiblePages: number[] = [];
        if (visibleOverwrittenPageIds) {
          item.relations.itemORM.meta.allPages.forEach((page) => {
            if (visibleOverwrittenPageIds.includes(page.pageId)) {
              overwrittenVisiblePages.push(page.number);
            }
          });
        }
        presentable = getPresentableFromDocVer(
          item.relations.itemORM,
          title,
          overwrittenVisiblePages.length ? overwrittenVisiblePages : item.model.visiblePages || [],
          undefined,
          authTokens,
          includeHiddenSlides,
          !visibleOverwrittenPageIds?.length,
        );
        presentable.orm = item;
      } else if (isCustomDeckORM(item.relations.itemORM)) {
        const customDeck = item.relations.itemORM;
        const { presentableGroups, presentablePages, numberOfPages } =
          getCustomDeckPresentableGroups(customDeck, authTokens, visibleOverwrittenPageIds, includeHiddenSlides);

        presentable = {
          id: uuid(),
          orm: item,
          title: item.model.customTitle || '',
          presentableGroups,
          presentablePages,
          numberOfPages,
        };
      }
  }

  if (!presentable) {
    throw new Error('Presentable is undefined');
  }
  const normalizedPresentable = {
    ...presentable,
    presentableGroups: presentable.presentableGroups.map((group) => ({
      ...group,
      pages: group.pages.map((page) => ({
        ...page,
        presentableGroup: group,
      })),
    })),
  };

  return normalizedPresentable;
}
